import {
  ACTION_CARD_COMPOSER_ASK,
  actionCardChipTitle,
  composerSeed,
  documentText,
  keptNodes,
  mentionChipNodes,
  strippedNodes,
  toDocument,
  type ComposerSeed,
  type ExistingComposerDraft,
} from '@littlebird/action-cards';

import { actionCardComposerDraft } from './action-card-mention-source';

import type { ActionCard } from '@littlebird/sdk';
import type { JSONContent } from '@tiptap/core';

export type ActionCardSeed =
  | { readonly kind: 'wait' }
  | { readonly kind: 'card'; readonly draft: ComposerSeed }
  | { readonly kind: 'fallback'; readonly draft: ComposerSeed };

// A type the client cannot decode still has the server's title
const cardChipDraft = (card: ActionCard): ComposerSeed | null => {
  const title = actionCardChipTitle(card) || card.title;
  if (!title) return null;
  return composerSeed([
    { type: 'paragraph', content: mentionChipNodes(card, title) },
  ]);
};

export const resolveActionCardSeed = ({
  card,
  storeLoaded,
}: {
  card: ActionCard | undefined;
  storeLoaded: boolean;
}): ActionCardSeed => {
  const draft = card
    ? (actionCardComposerDraft(card) ?? cardChipDraft(card))
    : null;
  if (draft) return { kind: 'card', draft };
  if (!storeLoaded) return { kind: 'wait' };
  return {
    kind: 'fallback',
    draft: { content: ACTION_CARD_COMPOSER_ASK, draftDocument: undefined },
  };
};

export const nextConsumedKey = (
  consumedKey: string | undefined,
  searchKey: string | undefined
): string | undefined => (searchKey === undefined ? undefined : consumedKey);

export const shouldConsumeSeed = (
  consumedKey: string | undefined,
  searchKey: string | undefined
): boolean => searchKey !== undefined && searchKey !== consumedKey;

const nodesOf = (document: JSONContent | undefined): JSONContent[] =>
  document?.content ?? [];

export const mergeSeedIntoDraft = (
  existing: ExistingComposerDraft | undefined,
  seed: ComposerSeed
): ComposerSeed => {
  const kept = strippedNodes(keptNodes(existing));
  const keptText = documentText(kept);
  if (kept.length === 0 || !keptText) return seed;

  return {
    content: `${seed.content}\n\n${keptText}`,
    draftDocument: {
      type: 'doc',
      content: [
        ...nodesOf(seed.draftDocument ?? toDocument(seed.content)),
        ...kept,
      ],
    },
  };
};
